import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { searchSite } from "@/content/search-index";
import { searchResultsPageNode } from "@/lib/entity-jsonld";

/**
 * Public site search. This route is the declared target of the WebSite
 * SearchAction (`/search?q={search_term_string}`) — if it ever moves, update
 * `SEARCH_PATH` in entity-jsonld.ts and the static block in index.html in the same change.
 *
 * Results pages are noindex (thin/duplicate content) while the route itself
 * stays crawlable and functional for the sitelinks searchbox.
 */
const SearchPage = () => {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const results = useMemo(() => searchSite(query), [query]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Search — Dr. Sully Speaks"
        description="Search masterclasses, coaching, advocacy services, books, eBooks, and articles across DrSullySpeaks.com."
        path="/search"
        noindex
        jsonLd={searchResultsPageNode()}
      />
      <SiteNav />

      <main className="mx-auto max-w-3xl px-5 pt-32 pb-24 md:pt-36">
        <h1 className="font-display text-4xl font-bold uppercase md:text-5xl">Search</h1>
        <p className="mt-4 text-muted-foreground">
          Find masterclasses, coaching, advocacy services, books, and articles.
        </p>

        <form
          role="search"
          className="mt-8 flex gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const value = new FormData(e.currentTarget).get("q");
            setParams(value ? { q: String(value) } : {});
          }}
        >
          <label htmlFor="site-search" className="sr-only">
            Search DrSullySpeaks.com
          </label>
          <div className="relative flex-1">
            <SearchIcon
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <input
              id="site-search"
              name="q"
              type="search"
              defaultValue={query}
              placeholder="Try “masterclass”, “advocacy”, or “speaking”"
              className="w-full rounded-md border border-border bg-card py-3 pl-10 pr-4 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary px-6 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            Search
          </button>
        </form>

        <div aria-live="polite" className="mt-10">
          {query && (
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
              {results.length} result{results.length === 1 ? "" : "s"} for “{query}”
            </p>
          )}

          {query && results.length === 0 && (
            <p className="mt-6 text-muted-foreground">
              Nothing matched that. Try a broader term, or{" "}
              <Link to="/ebooks" className="underline">
                browse the resource library
              </Link>
              .
            </p>
          )}

          <ul className="mt-6 space-y-px">
            {results.map((r) => (
              <li key={r.path} className="border border-border bg-card p-6">
                <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  {r.group}
                </span>
                <h2 className="mt-2 font-display text-xl">
                  <Link to={r.path} className="hover:underline">
                    {r.title}
                  </Link>
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{r.description}</p>
              </li>
            ))}
          </ul>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default SearchPage;
